import type { TFunction } from 'i18next'
import { format } from 'date-fns'
import { supabase } from '../../../lib/supabase'
import { reportUnexpectedError } from '../../../lib/reportError'

interface ExportParams {
  dateFrom: Date
  dateTo: Date
  memberQuery?: string
  activityTypes?: string[]
  actorIds?: string[]
  isAnonymised?: boolean
}

export interface ExportFilterLabels {
  dateRange: string
  activityTypes?: string[]
  actors?: string[]
  memberStatus?: string[]
  search?: string
}

interface RpcRow {
  id: string
  type: string
  stamps_delta: number
  rewards_delta: number
  created_at: string
  actor_name: string | null
  actor_role: string | null
  receipt_id: string | null
  correction_reason: string | null
  correction_note: string | null
  device_name: string | null
  reward_item_name: Record<string, string> | null
  loyalty_config_snapshot: { earning_method?: string; amount_per_stamp?: number } | null
  member_uuid: string
  member_first_name: string | null
  member_last_name: string | null
  member_email: string | null
  member_display_id: string | null
}

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function getFullName(firstName: string | null, lastName: string | null): string {
  return [firstName, lastName].filter(Boolean).join(' ') || '—'
}

function formatActorDisplay(name: string | null, role: string | null, t: TFunction): string {
  if (!name || role === 'system') return 'System'
  if (role === 'manager') return `${name} (${t('recentActivity.detail.managerRole')})`
  if (role === 'admin') return `${name} (${t('recentActivity.detail.adminRole')})`
  return name
}

function formatDelta(stampsDelta: number, rewardsDelta: number, t: TFunction): string {
  if (stampsDelta !== 0) {
    const sign = stampsDelta > 0 ? '+' : ''
    const abs = Math.abs(stampsDelta)
    const word = abs === 1 ? t('common.activity.stampSingle') : t('common.activity.stamps')
    return `${sign}${stampsDelta} ${word}`
  }
  if (rewardsDelta !== 0) {
    const sign = rewardsDelta > 0 ? '+' : ''
    const abs = Math.abs(rewardsDelta)
    const word = abs === 1 ? t('common.activity.rewardSingle') : t('common.activity.rewards')
    return `${sign}${rewardsDelta} ${word}`
  }
  return ''
}

function getActivityLabel(type: string, t: TFunction): string {
  if (type === 'new_member_joined') return t('recentActivity.activityTypes.new_member_joined')
  return t(`common.activity.type.${type}`, { defaultValue: type })
}

function buildFilterLines(labels: ExportFilterLabels, t: TFunction): string[] {
  const lines: string[] = []
  lines.push(`${t('recentActivity.csv.filterDateRange')}: ${labels.dateRange}`)
  if (labels.activityTypes?.length) {
    lines.push(`${t('recentActivity.filters.activityType')}: ${labels.activityTypes.join(', ')}`)
  }
  if (labels.actors?.length) {
    lines.push(`${t('recentActivity.filters.staff')}: ${labels.actors.join(', ')}`)
  }
  if (labels.memberStatus?.length) {
    lines.push(`${t('recentActivity.filters.memberStatus')}: ${labels.memberStatus.join(', ')}`)
  }
  if (labels.search) {
    lines.push(`${t('recentActivity.search.accessibilityLabel')}: ${labels.search}`)
  }
  return lines
}

export async function exportRecentActivityCsv(
  params: ExportParams,
  filterLabels: ExportFilterLabels,
  t: TFunction,
  lang: string,
): Promise<void> {
  const MAX_EXPORT = 10000
  const { data, error } = await supabase.rpc('get_recent_activity', {
    p_date_from: params.dateFrom.toISOString(),
    p_date_to: params.dateTo.toISOString(),
    p_page: 1,
    p_page_size: MAX_EXPORT,
    p_member_query: params.memberQuery || null,
    p_activity_types: params.activityTypes ?? null,
    p_actor_ids: params.actorIds ?? null,
    p_is_anonymised: params.isAnonymised ?? null,
  })

  if (error) throw error

  const result = data as { entries: RpcRow[]; total_count: number }

  // Everything from here on is local CSV-building and browser download
  // mechanics — no backend round trip — so failures are invisible anywhere
  // else and worth reporting.
  try {
    const filterLines = buildFilterLines(filterLabels, t)

    const headers = [
      t('recentActivity.csv.timestamp'),
      t('recentActivity.csv.activity'),
      t('recentActivity.csv.delta'),
      t('recentActivity.csv.member'),
      t('recentActivity.csv.memberEmail'),
      t('recentActivity.csv.memberId'),
      t('recentActivity.csv.performedBy'),
      t('recentActivity.csv.device'),
      t('recentActivity.csv.receipt'),
      t('recentActivity.csv.reason'),
      t('recentActivity.csv.note'),
    ]

    const csvRows = result.entries.map((row: RpcRow) => {
      const nameObj = row.reward_item_name
      const rewardName = nameObj ? (nameObj[lang] || nameObj.en || '') : ''
      const activityLabel = getActivityLabel(row.type, t)
      const label = rewardName ? `${activityLabel} — ${rewardName}` : activityLabel

      return [
        row.created_at,
        label,
        formatDelta(row.stamps_delta, row.rewards_delta, t),
        getFullName(row.member_first_name, row.member_last_name),
        row.member_email ?? '',
        row.member_display_id ?? '',
        formatActorDisplay(row.actor_name, row.actor_role, t),
        row.device_name ?? '',
        row.receipt_id ?? '',
        row.correction_reason ? t(`common.adjustmentReasons.${row.correction_reason}` as never) : '',
        row.correction_note ?? '',
      ]
    })

    const csv = [
      ...filterLines.map(escapeCsvField),
      '',
      '',
      headers.map(escapeCsvField).join(','),
      ...csvRows.map((row) => row.map(escapeCsvField).join(',')),
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `recent-activity-${format(new Date(), 'yyyy-MM-dd')}.csv`
    link.click()
    URL.revokeObjectURL(url)
  } catch (err) {
    reportUnexpectedError(err)
    throw err
  }
}
